import { STEP_AGENT_TYPES, WIZARD_STEPS } from '@/config/agentWizardSteps'
import { AGENT_DAILY_SEND_CAP } from '@/view/ai/agentTypes'

// Reasons the agent cannot be tried until the merchant changes something.
const STRUCTURAL_REASONS = ['no_cart', 'no_order', 'no_upsell_products', 'all_upsold', 'no_outreach']
const STOREFRONT_FALLBACK = 'your store'

export const isStructuralReason = code => STRUCTURAL_REASONS.includes(code)

const storefront = url => url || STOREFRONT_FALLBACK

const REASON_COPY = {
  no_test_contact: () => 'Add your mobile number first.',
  no_cart: ({ storefrontUrl }) =>
    `To try this one, open ${storefront(storefrontUrl)} on your phone, add something to your cart using this number, then come back.`,
  no_upsell_products: () => 'Add products to your Upsell category first.',
  no_order: ({ storefrontUrl }) =>
    `Place a paid order on ${storefront(storefrontUrl)} with this number for an upcoming date, then come back.`,
  no_outreach: () => 'Add a first message to this agent first.'
}

// Merchant-facing copy for an eligibility reason; anything else uses the server's reason.
export const reasonCopy = (reasonCode, { storefrontUrl, reason } = {}) =>
  REASON_COPY[reasonCode] ? REASON_COPY[reasonCode]({ storefrontUrl }) : reason || ''

const RELATIVE = new Intl.RelativeTimeFormat('en-US', { numeric: 'always' })
const MINUTE = 60 * 1000
const UNITS = [
  ['day', 24 * 60 * MINUTE],
  ['hour', 60 * MINUTE],
  ['minute', MINUTE]
]

// "2 minutes ago", "3 hours ago"; under a minute reads "just now".
export const relativeTime = (input, now = Date.now()) => {
  const diff = now - new Date(input).getTime()
  if (!(diff >= MINUTE)) return 'just now'
  const [unit, size] = UNITS.find(([, ms]) => diff >= ms)
  return RELATIVE.format(-Math.floor(diff / size), unit)
}

const plural = (count, one, many) => `${count} ${count === 1 ? one : many}`

const replyCount = session => (session.messages || []).filter(message => message.direction === 'customer').length

export const statusLine = (session, otherLiveName, now = Date.now()) => {
  if (!session) return 'Not sent yet'
  if (session.completed_at && !session.outcome && otherLiveName)
    return `Ended when you started the ${otherLiveName} test`
  return `Sent to you ${relativeTime(session.created_at, now)} · ${plural(replyCount(session), 'reply', 'replies')}`
}

// The wizard step key for an agent type ('WinbackAgent' -> 'winback').
export const stepKeyForType = type => Object.keys(STEP_AGENT_TYPES).find(key => STEP_AGENT_TYPES[key] === type)

// The wizard's plain name for an agent type ("Win back", "Abandoned carts").
export const agentPlainName = type => WIZARD_STEPS.find(step => step.key === stepKeyForType(type))?.label ?? 'Agent'

export const agentTestTitle = (type, storeName = '') =>
  storeName ? `${agentPlainName(type)} for ${storeName}` : agentPlainName(type)

// One line per agent in the "Go live with all" confirmation.
export const goLiveLine = (title, estimate, agentType) => {
  const cap = Number(estimate?.daily_send_cap) || AGENT_DAILY_SEND_CAP
  if (!estimate) return `${title}: no estimate right now, at most ${cap} a day`
  const count = Number(estimate.eligible_count) || 0
  const who =
    agentType === 'UpsellAgent'
      ? plural(count, 'order matches', 'orders match')
      : plural(count, 'customer matches', 'customers match')
  return `${title}: ${who} today, ${cap} a day`
}
